import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";
import API, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CheckCircle } from "lucide-react";

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const { lang } = useLanguage();
  const token = searchParams.get("token");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const texts = {
    nl: { title: "Nieuw wachtwoord instellen", password: "Nieuw wachtwoord", confirm: "Bevestig wachtwoord", submit: "Wachtwoord resetten", done_title: "Wachtwoord gewijzigd!", done_msg: "U kunt nu inloggen met uw nieuwe wachtwoord.", login: "Inloggen", mismatch: "Wachtwoorden komen niet overeen", no_token: "Ongeldige of ontbrekende token" },
    en: { title: "Set new password", password: "New password", confirm: "Confirm password", submit: "Reset password", done_title: "Password changed!", done_msg: "You can now log in with your new password.", login: "Login", mismatch: "Passwords do not match", no_token: "Invalid or missing token" },
    de: { title: "Neues Passwort festlegen", password: "Neues Passwort", confirm: "Passwort best\u00e4tigen", submit: "Passwort zur\u00fccksetzen", done_title: "Passwort ge\u00e4ndert!", done_msg: "Sie k\u00f6nnen sich jetzt mit Ihrem neuen Passwort anmelden.", login: "Anmelden", mismatch: "Passw\u00f6rter stimmen nicht \u00fcberein", no_token: "Ung\u00fcltiger oder fehlender Token" },
    fr: { title: "D\u00e9finir un nouveau mot de passe", password: "Nouveau mot de passe", confirm: "Confirmer le mot de passe", submit: "R\u00e9initialiser", done_title: "Mot de passe modifi\u00e9!", done_msg: "Vous pouvez maintenant vous connecter avec votre nouveau mot de passe.", login: "Se connecter", mismatch: "Les mots de passe ne correspondent pas", no_token: "Jeton invalide ou manquant" },
  };
  const t = texts[lang] || texts.en;

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-16 bg-[#F9FAFB] px-4">
        <div className="bg-white border border-border p-8 text-center">
          <p className="text-red-600 mb-4">{t.no_token}</p>
          <Link to="/login"><Button variant="outline" className="rounded-sm">{t.login}</Button></Link>
        </div>
      </div>
    );
  }

  if (done) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-16 bg-[#F9FAFB] px-4" data-testid="reset-done">
        <div className="w-full max-w-md bg-white border border-border p-8 text-center">
          <CheckCircle size={48} className="text-green-600 mx-auto mb-4" />
          <h2 className="text-xl font-bold mb-2">{t.done_title}</h2>
          <p className="text-sm text-muted-foreground mb-6">{t.done_msg}</p>
          <Link to="/login"><Button className="bg-[#0A3FA3] hover:bg-[#082F7A] rounded-sm" data-testid="reset-login-btn">{t.login}</Button></Link>
        </div>
      </div>
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (password !== confirm) { setError(t.mismatch); return; }
    setLoading(true);
    try {
      await API.post("/auth/reset-password", { token, password });
      setDone(true);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center pt-16 bg-[#F9FAFB] px-4" data-testid="reset-password-page">
      <div className="w-full max-w-md bg-white border border-border p-8">
        <h1 className="text-2xl font-bold tracking-tight mb-6">{t.title}</h1>
        <form onSubmit={handleSubmit} className="space-y-4">
          {error && <div className="text-sm text-red-600 bg-red-50 border border-red-200 p-3" data-testid="reset-error">{error}</div>}
          <div>
            <Label className="text-xs font-bold uppercase tracking-wider">{t.password}</Label>
            <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={6} className="mt-1 rounded-sm" data-testid="reset-password-input" />
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-wider">{t.confirm}</Label>
            <Input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required minLength={6} className="mt-1 rounded-sm" data-testid="reset-confirm-input" />
          </div>
          <Button type="submit" className="w-full bg-[#0A3FA3] hover:bg-[#082F7A] rounded-sm" disabled={loading} data-testid="reset-submit-btn">
            {loading ? "..." : t.submit}
          </Button>
        </form>
      </div>
    </div>
  );
}
